/**
 * The right-hand panel — a running log, each entry reading as what a student
 * did ("Added Dilute Sulfuric Acid") followed on the next, indented line by
 * WHAT THAT STEP CHANGED.
 *
 * "What changed" is the whole point, and it means two different things:
 *   - an action step reports only the facts that weren't already true before
 *     it, so a step doesn't restate the beaker's standing description;
 *   - a test step reports its READING, since that is what was asked for — and
 *     always, even when unchanged.
 *
 * Both were previously drawn from the same "describe the outcome" list, so
 * dipping the pH paper proudly reported "Exothermic Reaction, Gas evolved" and
 * left the reading itself in a badge behind "More".
 *
 * The full picture stays one click away in that detail panel; the summary line
 * carries the one thing that is news. A step with nothing new to say gets no
 * second line at all.
 *
 * Steps can be struck off individually. Exploring means a lot of small moves,
 * and a student should be able to clear the ones that led nowhere without
 * emptying the whole container to do it.
 */
import { useState } from 'react';
import type { LabStep } from '../labState';
import type { ChemistryLabOutcome } from '../types';
import { changedItems, testItems } from '../outcomeDisplay';
import { OutcomePanel } from './OutcomePanel';
import { Working } from './Working';

interface StepLogProps {
  steps: LabStep[];
  loading: boolean;
  /** How many substances the container holds right now — a single one has
   *  nothing to react WITH, so the pending label below says "checking…"
   *  rather than "reacting…", which is only true once there's a second
   *  substance (or heat) actually acting on it. */
  substanceCount: number;
  /** Whether an entry says how much went in. The amount is recorded on every
   *  step either way — this only decides which of the two sentences is read,
   *  so turning it on shows the quantities of steps already taken. */
  showQuantities: boolean;
  error?: string;
}

/**
 * What a step CHANGED, and the full outcome one click behind it — the part of
 * a log entry that is about chemistry rather than about the log.
 *
 * Exported because a guided run shows the same thing under each completed
 * instruction (GuidedPanel): the step a script asked for and the step a
 * student chose are the same step, and should read identically.
 */
export function StepResult({ step, previousOutcome, previousCelsius, expanded, onToggle }: {
  step: LabStep;
  /** What was already true before this step — an action only reports what it
   *  changed against this. */
  previousOutcome?: ChemistryLabOutcome;
  /** The burner setting `previousOutcome` was computed at — needed alongside
   *  it so a "Boiling" that was already true doesn't get repeated. */
  previousCelsius?: number;
  /**
   * Whether the full outcome is showing. CONTROLLED by whoever draws the list,
   * because only they can know which step is the newest — and only one step is
   * open at a time. It used to be each step's own business, seeded once on
   * arrival, which meant a log left open at every step a student had ever
   * clicked and the new one buried under them.
   */
  expanded: boolean;
  onToggle: () => void;
}): React.JSX.Element | null {
  const headline = !step.outcome
    ? []
    : step.kind === 'test'
      ? testItems(step.outcome)
      : changedItems(step.outcome, previousOutcome, step.temperatureC, previousCelsius);
  const result = headline.map(h => h.text).join(', ');
  // An action step that computed a real outcome but changed nothing visible
  // still deserves a line — otherwise a blank step reads as "did this even
  // do anything?" rather than "checked, and nothing happened" (e.g. copper
  // in dilute sulfuric acid, room temp or hot: genuinely no reaction). A test
  // step never lands here — testItems always reports its reading.
  const noChange = step.kind === 'action' && !!step.outcome && headline.length === 0;

  // A step can have nothing to REPORT and still have something to work out —
  // a mix whose only visible change is a number.
  if (!result && !noChange) {
    // No headline means no More button, so nothing here could reveal the
    // table later — show it in full rather than leave it unreachable.
    return step.stoichiometry || step.stoichiometryUnavailable
      ? <Working
          stoichiometry={step.stoichiometry}
          unavailable={step.stoichiometryUnavailable}
          expanded
        />
      : null;
  }
  return (
    <>
      {result && (
        <div className="chemlab-step-result">
          <span className="chemlab-step-result-text">{result}</span>
          <button className="chemlab-step-more" onClick={onToggle}>
            {expanded ? 'Less' : 'More'}
          </button>
        </div>
      )}
      {noChange && (
        <div className="chemlab-step-result chemlab-step-result-muted">
          <span className="chemlab-step-result-text">No visible change</span>
          <button className="chemlab-step-more" onClick={onToggle}>
            {expanded ? 'Less' : 'More'}
          </button>
        </div>
      )}
      {/* The conclusion always; the table of moles with the step's own More.
          A notebook of five pours each carrying a full table was a wall of
          numbers — the line says what happened, and More is how you ask how
          much. One control for the step, not a second one for its working. */}
      <Working
        stoichiometry={step.stoichiometry}
        unavailable={step.stoichiometryUnavailable}
        expanded={expanded}
      />
      {expanded && step.outcome && (
        <div className="chemlab-step-detail">
          <OutcomePanel outcome={step.outcome} source={step.source} celsius={step.temperatureC} />
        </div>
      )}
    </>
  );
}

/*
 * A step cannot be removed.
 *
 * There was a ✕ on every entry, and all it did was filter the entry out of the
 * log — the substances, the temperature and the outcome it produced all stayed
 * in the vessel. So it deleted the record of an action whose effect was still
 * there, and the log stopped describing the bench in front of you. Undoing
 * chemistry means undoing the mix, which is what Clear Bench is for.
 */
function StepEntry({ step, previousOutcome, previousCelsius, expanded, onToggle, showQuantities }: {
  step: LabStep;
  previousOutcome?: ChemistryLabOutcome;
  previousCelsius?: number;
  expanded: boolean;
  onToggle: () => void;
  showQuantities: boolean;
}): React.JSX.Element {
  return (
    <div className="chemlab-step">
      <div className="chemlab-step-head">
        <p className="chemlab-step-label">
          {(showQuantities && step.labelWithAmount) || step.label}
        </p>
      </div>
      <StepResult
        step={step} previousOutcome={previousOutcome} previousCelsius={previousCelsius}
        expanded={expanded} onToggle={onToggle}
      />
    </div>
  );
}

export function StepLog({ steps, loading, substanceCount, showQuantities, error }: StepLogProps): React.JSX.Element {
  /**
   * One step open at a time, and it is the newest.
   *
   * A result is read once, when it arrives — after that the log is a list of
   * what has been done, and every entry left open is a screen of detail
   * between the student and the thing that just happened. So the log is an
   * accordion whose newest entry opens itself: `null` means the student closed
   * it, which is respected until another step arrives.
   */
  const lastId = steps[steps.length - 1]?.id;
  const [openId, setOpenId] = useState<string | undefined>(lastId);
  const [seenLast, setSeenLast] = useState(lastId);
  if (lastId !== seenLast) {
    // Rendered, not in an effect: the new step and its open state land in the
    // same paint, so nothing is ever seen closed for a frame and then opened.
    setSeenLast(lastId);
    setOpenId(lastId);
  }
  return (
    <div className="chemlab-step-log">
      {steps.length === 0 && !loading && !error && (
        <p className="chemlab-empty-hint">Add a substance to the selected container to begin.</p>
      )}
      {steps.map((step, i) => (
        <StepEntry
          key={step.id}
          step={step}
          previousOutcome={steps[i - 1]?.outcome}
          previousCelsius={steps[i - 1]?.temperatureC}
          expanded={openId === step.id}
          onToggle={() => setOpenId(id => (id === step.id ? undefined : step.id))}
          showQuantities={showQuantities}
        />
      ))}
      {loading && <p className="chemlab-step-pending">{substanceCount <= 1 ? 'checking…' : 'reacting…'}</p>}
      {error && <p className="chemlab-error">{error}</p>}
    </div>
  );
}
